/**
 * src/components/ui.jsx — the handful of presentational pieces every page uses.
 *
 * Kept in one file because each is a few lines and splitting them across eight
 * files would make them harder to find, not easier.
 */

/**
 * Coloured status pill. The class name is derived from the status so the CSS
 * in styles.css (`.pill-signed`, `.pill-cancelled`, …) picks the colour.
 *
 * @param {{ status: string|null|undefined }} props
 * @returns {JSX.Element|null}
 */
export function Pill({ status }) {
  if (!status) return null;
  return <span className={`pill pill-${status}`}>{status.replace(/_/g, ' ')}</span>;
}

/**
 * Error banner showing the server's own message.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element|null}
 */
export function ErrorBanner({ children }) {
  if (!children) return null;
  return <div className="alert alert-error">{children}</div>;
}

/**
 * Placeholder for a list with nothing in it. Always says what would be here
 * and, where useful, how to add the first one — an empty table with no
 * explanation is the most common way a dashboard feels broken.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Empty({ children }) {
  return <div className="empty">{children}</div>;
}

/**
 * Page heading with an optional action on the right.
 *
 * @param {{ title: string, subtitle?: string, actions?: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="row-between" style={{ marginBottom: 20 }}>
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="muted small">{subtitle}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}

/**
 * Modal dialog. Closes on backdrop click and on Escape, because a dialog that
 * traps you is worse than no dialog.
 *
 * @param {{ title: string, onClose: () => void, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Modal({ title, onClose, children }) {
  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      role="presentation"
    >
      {/* Stop clicks inside the dialog from reaching the backdrop handler. */}
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="row-between" style={{ marginBottom: 16 }}>
          <h2>{title}</h2>
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Loading and error wrapper for a page's main fetch.
 *
 * Saves every page writing the same three-way branch, and makes sure a failed
 * fetch always shows the server's message rather than an empty page.
 *
 * @param {{ loading: boolean, error: string|null, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Loader({ loading, error, children }) {
  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (loading) return <div className="empty loading-pulse">Loading…</div>;
  return children;
}
